import { useTopicLocale } from "@/hooks/use-topic-locale";
import {
  Building2,
  Home,
  MessageCircle,
  Newspaper,
  Search,
} from "lucide-react";
import { Link, useLocation } from "wouter";

interface TabProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  active?: boolean;
  anchor?: boolean;
}

function Tab({ icon, label, path, active = false, anchor = false }: TabProps) {
  const content = (
    <span
      className={`flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center py-1 transition-colors ${
        active ? "text-brand-accent" : "text-ink-3"
      }`}
      aria-current={active ? "page" : undefined}
    >
      <span className="text-[18px]">{icon}</span>
      <span className="mt-0.5 text-[11px] leading-none">{label}</span>
    </span>
  );

  return anchor ? (
    <a href={path} className="flex flex-1">
      {content}
    </a>
  ) : (
    <Link href={path} className="flex flex-1">
      {content}
    </Link>
  );
}

export function BottomTab() {
  const [location] = useLocation();
  const { isKorean } = useTopicLocale();

  // 5개 고정 (docs/OZK_FINAL_LAUNCH_STRATEGY.md 2장): 홈 · 생활문제 · 뉴스 ·
  // 업체 · 커뮤니티. 마켓은 별도 탭을 두지 않고 홈의 바로가기와 커뮤니티
  // (동네 톡톡) 페이지 내부의 "동네마켓" 카테고리 연결로만 접근한다
  // (client/src/pages/CommunityTalk.tsx의 handleCategoryClick 참조).
  const tabs = [
    {
      path: "/",
      label: isKorean ? "홈" : "Home",
      icon: <Home className="h-5 w-5" />,
    },
    {
      path: "/topics",
      label: isKorean ? "생활문제" : "Solutions",
      icon: <Search className="h-5 w-5" />,
    },
    {
      path: "/news",
      label: isKorean ? "뉴스" : "News",
      icon: <Newspaper className="h-5 w-5" />,
    },
    {
      path: "/business-directory",
      label: isKorean ? "업체" : "Services",
      icon: <Building2 className="h-5 w-5" />,
    },
    {
      path: "/community-talk",
      label: isKorean ? "커뮤니티" : "Community",
      icon: <MessageCircle className="h-5 w-5" />,
    },
  ];

  const isTabActive = (path: string) => {
    if (path === "/") return location === "/";
    if (path.includes("#")) return false;
    return location.startsWith(path);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 flex h-14 border-t border-line-subtle bg-surface-0"
      aria-label={isKorean ? "모바일 주요 메뉴" : "Mobile navigation"}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.path}
          icon={tab.icon}
          label={tab.label}
          path={tab.path}
          active={isTabActive(tab.path)}
        />
      ))}
    </nav>
  );
}
